import React, { useEffect, useRef, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { Box, CircularProgress, Divider, Skeleton, Stack } from '@mui/material'
import { useRouteMatch } from 'react-router-dom'

import { DateFilter } from '@trivie/ui-web/components/date-filter'
import { Insight } from '@trivie/ui-web/components/insight'
import { Mastery } from '@trivie/ui-web/components/mastery'
import { Punchcard } from '@trivie/ui-web/components/punchcard'
import { Text } from '@trivie/ui-web/components/text'
import { GenerateReport } from '@trivie/ui-web/components/generate-report'
import { StrongestWeakest } from '@trivie/ui-web/components/strongest-weakest'
import { useStores } from '@trivie/core'
import { Panel } from '@trivie/ui-web/components/panel/panel'
import { TagApi } from '@trivie/core/src/services/api-objects'

const Overview: React.FunctionComponent = observer(() => {
  const { tagStore } = useStores()
  const {
    overviewStats,
    accomplishingStats,
    hourlyActivity,
    knowledgeGainStats,
    setSelectedTagId,
    mastery,
    getTagOverviewStats,
    getTagAccomplishingStats,
    getTagHourlyActivity,
    getTagKnowledgeGainStats,
    getTagTopicGaps,
    getTagQuestionGaps,
    getTagMastery,
    topicGaps,
    questionGaps,
  } = tagStore

  const match = useRouteMatch<any>('/people/tags/:id/overview')
  const [timespan, setTimespan] = useState('All Time')

  const [loadingTagStats, setLoadingTagStats] = useState(true)
  const [loadingAccomplishments, setLoadingAccomplishments] = useState(true)
  const [loadingHourly, setLoadingHourly] = useState(true)
  const [loadingMastery, setLoadingMastery] = useState(true)
  const [loadingTopicGaps, setLoadingTopicGaps] = useState(true)
  const [loadingQuestionGaps, setLoadingQuestionGaps] = useState(true)
  const [loadingKnowledgeGain, setLoadingKnowledgeGain] = useState(true)

  const tagStatsCancelRef = useRef<any>()
  const accomplishmentsCancelRef = useRef<any>()
  const hourlyCancelRef = useRef<any>()
  const masteryCancelRef = useRef<any>()
  const topicGapRef = useRef<any>()
  const questionGapRef = useRef<any>()
  const knowledgeGainRef = useRef<any>()

  useEffect(() => {
    return () => {
      cancelRequests()
    }
  }, [])

  const cancelRequests = () => {
    if (topicGapRef.current) {
      topicGapRef.current.cancel()
    }
    if (questionGapRef.current) {
      questionGapRef.current.cancel()
    }
    if (tagStatsCancelRef.current) {
      tagStatsCancelRef.current.cancel()
    }
    if (accomplishmentsCancelRef.current) {
      accomplishmentsCancelRef.current.cancel()
    }
    if (masteryCancelRef.current) {
      masteryCancelRef.current.cancel()
    }
    if (hourlyCancelRef.current) {
      hourlyCancelRef.current.cancel()
    }
    if (knowledgeGainRef.current) {
      knowledgeGainRef.current.cancel()
    }
  }

  const load = (startDate: string, endDate: string) => {
    cancelRequests()
    setLoadingTagStats(true)
    setLoadingAccomplishments(true)
    setLoadingHourly(true)
    setLoadingMastery(true)
    setLoadingTopicGaps(true)
    setLoadingQuestionGaps(true)
    setLoadingKnowledgeGain(true)

    const tagStatsCancel = TagApi.getCancelToken()
    tagStatsCancelRef.current = tagStatsCancel
    getTagOverviewStats(startDate, endDate, tagStatsCancel.token)
      .then(() => setLoadingTagStats(false))
      .catch((e) => setLoadingTagStats(true))

    const accomplishmentCancel = TagApi.getCancelToken()
    accomplishmentsCancelRef.current = accomplishmentCancel
    getTagAccomplishingStats(startDate, endDate, accomplishmentCancel.token)
      .then(() => setLoadingAccomplishments(false))
      .catch((e) => setLoadingAccomplishments(true))

    const hourlyCancel = TagApi.getCancelToken()
    hourlyCancelRef.current = hourlyCancel
    getTagHourlyActivity(startDate, endDate, hourlyCancel.token)
      .then(() => setLoadingHourly(false))
      .catch((e) => setLoadingHourly(true))

    const knowledgeGainCancel = TagApi.getCancelToken()
    knowledgeGainRef.current = knowledgeGainCancel
    getTagKnowledgeGainStats(startDate, endDate, knowledgeGainCancel.token)
      .then(() => setLoadingKnowledgeGain(false))
      .catch((e) => setLoadingKnowledgeGain(true))

    const topicGapsCancel = TagApi.getCancelToken()
    topicGapRef.current = topicGapsCancel
    getTagTopicGaps(startDate, endDate, topicGapsCancel.token)
      .then(() => setLoadingTopicGaps(false))
      .catch((e) => setLoadingTopicGaps(true))

    const questionGapsCancel = TagApi.getCancelToken()
    questionGapRef.current = questionGapsCancel
    getTagQuestionGaps(startDate, endDate, questionGapsCancel.token)
      .then(() => setLoadingQuestionGaps(false))
      .catch((e) => setLoadingQuestionGaps(true))

    const masteryCancel = TagApi.getCancelToken()
    masteryCancelRef.current = masteryCancel
    getTagMastery(startDate, endDate, masteryCancel.token)
      .then(() => setLoadingMastery(false))
      .catch((e) => setLoadingMastery(true))
  }

  const onDateChange = (start, end, label) => {
    setTimespan(label as any)
    setSelectedTagId(Number(match?.params.id))
    load(start, end)
  }

  return (
    <>
      <Stack mb={2} direction="row" justifyContent="space-between" alignItems="center">
        <DateFilter onDateChange={onDateChange} />
        <GenerateReport filename="tag-detail" />
      </Stack>
      <Stack useFlexGap gap={2} pb={4}>
        {loadingKnowledgeGain ? (
          <Box minHeight={194} flex={1} sx={{ width: '100%', height: '100%' }}>
            <Skeleton sx={{ transform: 'none', height: '100%', borderRadius: '20px' }} />
          </Box>
        ) : (
          <Panel title="How much are people with this tag learning?">
            <Stack gap={2} direction="row" flexWrap="wrap">
              <Insight
                timespan={timespan}
                data={knowledgeGainStats?.stats_history[2].data_points}
                title="Baseline Knowledge"
                unit="%"
                value={`${knowledgeGainStats?.starting_knowledge?.toFixed(1) ?? '0'}%`}
              />
              <Insight
                timespan={timespan}
                data={knowledgeGainStats?.stats_history[1].data_points}
                title="Current Knowledge"
                unit="%"
                value={`${knowledgeGainStats?.current_knowledge?.toFixed(1) ?? '0'}%`}
              />
              <Insight
                data={knowledgeGainStats?.stats_history[0].data_points}
                title="Knowledge Gain"
                unit="%"
                value={`${knowledgeGainStats?.knowledge_gain?.toFixed(1) ?? '0'}%`}
              />
            </Stack>
            <Text
              variant="body2Medium"
              color="text.secondary"
              text="We measure each person's baseline knowledge by their first answer to each question. As they answer more questions, their current knowledge rises, resulting in knowledge gain."
            />
          </Panel>
        )}
        <Stack useFlexGap gap={2} flexWrap="wrap" direction="row">
          {loadingTagStats ? (
            <Box minHeight={113} flex={1} sx={{ width: '100%', height: '100%' }}>
              <Skeleton sx={{ transform: 'none', height: '100%', borderRadius: '20px' }} />
            </Box>
          ) : (
            <Panel sx={{ flex: 1 }}>
              <Insight
                timespan={timespan}
                data={overviewStats?.stats_history[0].data_points}
                title="Active Users"
                outlined
                unit=" Users"
                value={overviewStats?.active_users ?? '0'}
                style={{ marginRight: 20 }}
              />
            </Panel>
          )}
          {loadingTagStats ? (
            <Box minHeight={113} flex={1} sx={{ width: '100%', height: '100%' }}>
              <Skeleton sx={{ transform: 'none', height: '100%', borderRadius: '20px' }} />
            </Box>
          ) : (
            <Panel sx={{ flex: 1 }}>
              <Insight
                timespan={timespan}
                data={overviewStats?.stats_history[1].data_points}
                title="Average Mastery"
                outlined
                value={`${overviewStats?.mastery?.toFixed(1) ?? 0}`}
                unit=""
                ofVal="/ 4"
                style={{ marginRight: 20 }}
              />
            </Panel>
          )}
          {loadingTagStats ? (
            <Box minHeight={113} flex={1} sx={{ width: '100%', height: '100%' }}>
              <Skeleton sx={{ transform: 'none', height: '100%', borderRadius: '20px' }} />
            </Box>
          ) : (
            <Panel sx={{ flex: 1 }}>
              <Insight
                timespan={timespan}
                data={overviewStats?.stats_history[2].data_points}
                title="Correct Answer %"
                unit="%"
                value={`${overviewStats?.pct_correct?.toFixed(1) ?? 0} %`}
                outlined
              />
            </Panel>
          )}
        </Stack>
        <Stack useFlexGap gap={2} flexWrap="wrap" direction="row">
          {loadingAccomplishments ? (
            <Box height={500} flex={1} sx={{ width: '100%', height: '100%' }}>
              <Skeleton sx={{ transform: 'none', height: '100%', borderRadius: '20px' }} />
            </Box>
          ) : (
            <Panel style={{ flex: 1 }} title="What are people with this tag accomplishing?">
              <Insight
                sx={{ display: 'flex' }}
                timespan={timespan}
                unit=" Questions"
                data={accomplishingStats?.stats_history[0].data_points}
                title="Questions Answered"
                value={accomplishingStats?.questions_answered ?? '0'}
              />
              <Divider />
              <Insight
                sx={{ display: 'flex' }}
                timespan={timespan}
                unit=" Quizzes"
                data={accomplishingStats?.stats_history[1].data_points}
                title="Quizzes Completed"
                value={accomplishingStats?.quizzes_completed ?? '0'}
              />
              <Divider />
              <Insight
                sx={{ display: 'flex' }}
                timespan={timespan}
                unit=" Sources"
                data={accomplishingStats?.stats_history[2].data_points}
                title="Sources Viewed"
                value={accomplishingStats?.resources_viewed ?? '0'}
              />
            </Panel>
          )}
          {loadingHourly ? (
            <Box height={500} sx={{ width: '100%' }}>
              <Skeleton sx={{ transform: 'none', height: '100%', borderRadius: '20px' }} />
            </Box>
          ) : (
            <Panel style={{ flex: 2 }} divider title="When are people with this tag learning most?">
              <Punchcard values={hourlyActivity} />
            </Panel>
          )}
        </Stack>
        {loadingMastery ? (
          <Box minHeight={194} flex={1} sx={{ width: '100%', height: '100%' }}>
            <Skeleton sx={{ transform: 'none', height: '100%', borderRadius: '20px' }} />
          </Box>
        ) : (
          <Panel title="Mastery Distribution">
            <Stack gap={3} direction="row" flexWrap="wrap">
              <Mastery
                variant="green"
                percentage={mastery?.most}
                message={`${mastery?.most_user_count} people with this tag have mastered most of their questions.`}
              />
              <Mastery
                variant="yellow"
                percentage={mastery?.some}
                message={`${mastery?.some_user_count} people with this tag have mastered some of their questions.`}
              />
              <Mastery
                variant="red"
                percentage={mastery?.few}
                message={`${mastery?.few_user_count} people with this tag have mastered few of their questions.`}
              />
            </Stack>
          </Panel>
        )}
        <Stack flexWrap="wrap" direction="row" useFlexGap gap={2}>
          {(!loadingTopicGaps && topicGaps?.weakest_topics?.length) ||
          (!loadingQuestionGaps && questionGaps?.weakest_questions?.length) ? (
            <StrongestWeakest
              type="Weakest"
              variant="medium"
              topics={topicGaps?.weakest_topics}
              questions={questionGaps?.weakest_questions}
              style={{ flex: 1 }}
            />
          ) : (!loadingTopicGaps && !topicGaps?.weakest_topics?.length) ||
            (!loadingQuestionGaps && !questionGaps?.weakest_questions?.length) ? null : (
            <Box minHeight={280} flex={1} sx={{ width: '100%', height: '100%' }}>
              <Skeleton sx={{ transform: 'none', height: '100%', borderRadius: '20px' }} />
            </Box>
          )}
          {(!loadingTopicGaps && topicGaps?.strongest_topics?.length) ||
          (!loadingQuestionGaps && questionGaps?.strongest_questions?.length) ? (
            <StrongestWeakest
              type="Weakest"
              variant="medium"
              topics={topicGaps?.strongest_topics}
              questions={questionGaps?.strongest_questions}
              style={{ flex: 1 }}
            />
          ) : (!loadingTopicGaps && !topicGaps?.strongest_topics?.length) ||
            (!loadingQuestionGaps && !questionGaps?.strongest_questions?.length) ? null : (
            <Box minHeight={280} flex={1} sx={{ width: '100%', height: '100%' }}>
              <Skeleton sx={{ transform: 'none', height: '100%', borderRadius: '20px' }} />
            </Box>
          )}
        </Stack>
      </Stack>
    </>
  )
})

export const TagsOverviewTab = (props: any) => {
  const { id } = props
  return {
    name: 'Overview',
    path: `/people/tags/${id}/overview`,
    component: () => <Overview />,
  }
}
